import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  TrendingDown,
  ShieldCheck,
  Unlock,
  Handshake,
  Sparkles,
  Calculator,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SidebPill } from "@/components/SidebPill";
import { ProofTldr } from "@/components/ProofTldr";
import CaseStudyFaq from "@/components/CaseStudyFaq";
import { openChat } from "@/lib/chat";
import { useHashScroll } from "@/hooks/useHashScroll";
import { Seo, articleSchema, faqSchema, breadcrumbSchema } from "@/components/Seo";

const FAQS = [
  { q: "What is a Studio Retainer and what does it include?", a: "A Studio Retainer is a monthly engagement where SideB plugs into your revenue or payments org as senior on-call leadership. Standard inclusions: a monthly strategy session, quarterly board-prep, on-demand vendor reviews, and async Slack access. Scope is mutually defined at activation and revisited quarterly. Month-to-month with a 30-day cancellation notice; no annual commitment." },
  { q: "How does SideB help exit a card-scheme fraud monitoring program?", a: "End-to-end. We diagnose the root causes of the elevated fraud rate (rules, BIN routing, 3DS posture, BIN-attack patterns), remediate them, document the remediation in scheme-readable format, and run the formal program-exit submission with the acquirer and the schemes. In the case studied here, the fraud rate moved from 2% to under 0.5%, the program was officially exited, and frozen acquirer reserve was released back to the business." },
  { q: "How fast can a merchant get out of an EFM or VFMP program?", a: "Typical exit timelines run 90–180 days from engagement start, depending on the program type, the current month's fraud-to-volume ratio, and the acquirer's review cadence. Visa's EFM and Mastercard's VFMP have different exit criteria — we navigate both. Faster exits are possible when remediation is already in flight before SideB engages." },
  { q: "Will a Studio Retainer replace our need for in-house payments leadership?", a: "Not as a permanent substitute for a senior FTE — but it's the right shape while you're growing into one, when a niche skill set (fraud, scheme-program, BIN routing) isn't worth a full-time hire, or when leadership wants senior judgment alongside an internal team that's stretched thin. Many clients run Retainer alongside their internal Head of Payments." },
  { q: "Can the Retainer scope change after activation?", a: "Yes — scope is revisited every quarter. As the business evolves (M&A, new geos, regulatory changes, new product surfaces), the work mix shifts. We also pause or scale down without penalty if priorities change. The cancel-anytime structure keeps us accountable to deliver value every month." },
];

const KPIS = [
  { value: "2% → <0.5%", label: "Non-friendly fraud rate, in under one program cycle", icon: TrendingDown },
  { value: "Exited", label: "Visa/Mastercard fraud monitoring program", icon: ShieldCheck },
  { value: "Released", label: "Frozen reserve capital unlocked on a schedule", icon: Unlock },
  { value: "Restored", label: "PSP good standing, reserve terms renegotiated", icon: Handshake },
];

export default function ProofStudioRetainer() {
  useHashScroll();

  return (
    <div className="bg-white" data-testid="proof-studio-retainer-page">
      <Seo
        title="How SideB helped a high-volume merchant exit a Visa/Mastercard fraud monitoring program | SideB Case Study"
        description="Case study: SideB's Studio Retainer drove a merchant's fraud rate from 2% to under 0.5%, exited a Visa/Mastercard fraud monitoring program, and unlocked frozen acquirer reserve capital — restoring PSP good standing."
        canonical="https://sideb.io/proof/studio-retainer"
        ogType="article"
        keywords="Visa EFM exit, Mastercard VFMP exit, fraud rate remediation, scheme fraud program, PSP reserve release, Studio Retainer, fractional payments leadership"
        schema={[
          articleSchema({
            headline: "How SideB helped a high-volume merchant exit a Visa/Mastercard fraud monitoring program",
            description: "SideB's Studio Retainer drove fraud from 2% to under 0.5%, exited a scheme fraud monitoring program, and unlocked frozen acquirer reserve capital.",
            slug: "proof/studio-retainer",
            datePublished: "2026-01-01",
            section: "Case Study",
          }),
          faqSchema(FAQS),
          breadcrumbSchema([
            { name: "Home", url: "https://sideb.io/" },
            { name: "Proof", url: "https://sideb.io/proof" },
            { name: "Studio Retainer Case Study", url: "https://sideb.io/proof/studio-retainer" },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0a1628] text-white">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          aria-hidden
          style={{
            background:
              "radial-gradient(60% 50% at 25% 30%, rgba(172,98,234,0.45) 0%, rgba(11,30,90,0) 70%)",
            filter: "blur(40px)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <Link
            to="/proof"
            className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white tracking-widest uppercase mb-6"
            data-testid="proof-studio-back-to-hub"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All case studies
          </Link>
          <div className="label-mono text-white/70">Proof · Studio Retainer · Fraud remediation & capital recovery</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 tracking-[-0.035em] leading-[1.05] font-semibold max-w-4xl">
            From a 2% fraud rate and a frozen reserve <span className="text-[#c9a3f4]">to good standing and capital back in the business.</span>
          </h1>
          <p className="text-lg text-white/80 mt-6 max-w-2xl leading-relaxed">
            A high-volume digital merchant placed into a Visa/Mastercard fraud monitoring program by their acquirer — with mounting fines and aggressive reserve terms freezing operational capital. The Studio Retainer owned the remediation playbook end-to-end.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3" data-testid="proof-studio-hero-ctas">
            <Button asChild className="btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] text-white h-11 px-6">
              <Link to="/services/studio-retainer" data-testid="proof-studio-cta-service">
                Explore the Studio Retainer <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="btn-pill h-11 px-6 border-white/30 text-white hover:bg-white/10 hover:text-white"
            >
              <Link to="/inquiry" data-testid="proof-studio-cta-brief">Send a brief</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* KPI grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 -mt-12 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {KPIS.map((k) => {
            const Icon = k.icon;
            return (
              <div
                key={k.label}
                className="card-sharp p-5 bg-white shadow-[0_18px_40px_-12px_rgba(11,30,90,0.18)]"
                data-testid={`proof-studio-kpi-${k.label.toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}
              >
                <div className="w-9 h-9 rounded-lg bg-[#ac62ea]/12 flex items-center justify-center">
                  <Icon className="h-4 w-4 text-[#ac62ea]" />
                </div>
                <div className="font-display text-3xl mt-3 font-semibold tracking-tight">{k.value}</div>
                <div className="text-xs text-muted-foreground mt-1 leading-snug">{k.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* TL;DR */}
      <ProofTldr
        testid="proof-studio-tldr"
        problem="A high-volume merchant placed into a Visa/Mastercard fraud monitoring program — 2% non-friendly fraud rate, escalating fines, and an acquirer reserve that froze working capital."
        approach="Studio Retainer — audited the full payment stack (auth flow, BIN routing, 3DS, velocity rules, BIN-attack patterns), restored compliance, then ran the formal program exit with the schemes and the acquirer."
        outcome="Fraud rate driven from 2% to under 0.5%, merchant officially exited from the scheme program, and frozen reserve capital released back to the business."
      />

      {/* Body sections */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 space-y-14">
        {/* Executive Summary */}
        <section data-testid="proof-studio-summary">
          <div className="label-mono">Executive summary</div>
          <h2 className="font-display text-3xl mt-2 tracking-tight font-semibold">How SideB helped a high-volume merchant exit a Visa/Mastercard fraud monitoring program and unlock frozen acquirer reserves.</h2>
          <p className="mt-5 text-foreground/85 leading-relaxed text-lg">
            A high-volume digital merchant&apos;s non-friendly fraud rate had climbed to <b>2%</b> — well above the threshold that triggers card-scheme enforcement. The acquirer placed them into a Visa/Mastercard fraud monitoring program, fines escalated, and the PSP responded with aggressive reserve terms that froze operational capital the business needed to run.
          </p>
          <p className="mt-4 text-foreground/85 leading-relaxed text-lg">
            Under the <b>Studio Retainer</b>, <SidebPill /> stepped in as the operating partner across payments, fraud, and acquirer relationships — diagnosed the root cause, executed the remediation, restored compliance standing, and renegotiated the reserve back to the merchant&apos;s balance sheet.
          </p>
        </section>

        {/* Challenge */}
        <section id="challenge" className="scroll-mt-24" data-testid="proof-studio-challenge">
          <div className="label-mono">The challenge</div>
          <h2 className="font-display text-3xl mt-2 tracking-tight font-semibold">A 2% fraud rate, a card-scheme program placement, and capital frozen at the worst possible time.</h2>
          <ul className="mt-5 space-y-3 text-foreground/85 leading-relaxed">
            <li className="flex gap-3"><Check className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" /> <span><b>Non-friendly fraud at 2%.</b> Well above scheme tolerance — every additional cycle in the program meant more fines, tighter scrutiny, and harder downstream consequences with the acquirer.</span></li>
            <li className="flex gap-3"><Check className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" /> <span><b>Forced into a Visa/Mastercard fraud monitoring program.</b> Card-scheme enforcement triggered scheme-level fines, mandatory remediation reporting, and elevated risk treatment at every level of the stack.</span></li>
            <li className="flex gap-3"><Check className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" /> <span><b>PSP imposed aggressive reserve terms.</b> Operational capital frozen on the acquirer&apos;s balance sheet — the kind of working-capital squeeze that compounds fast on a growth-stage business with real revenue obligations.</span></li>
            <li className="flex gap-3"><Check className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" /> <span><b>Internal teams without the playbook.</b> Fraud, finance, and engineering knew their systems individually — but no one in the room had run a scheme-program exit before, and the timeline didn&apos;t allow for learning on the fly.</span></li>
            <li className="flex gap-3"><Check className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" /> <span><b>Acquirer trust eroding fast.</b> Each monthly review without measurable improvement increased the odds of escalated reserve terms or, at the extreme, processing limits and account termination.</span></li>
          </ul>
        </section>

        {/* Solution */}
        <section id="solution" className="scroll-mt-24" data-testid="proof-studio-solution">
          <div className="label-mono">The solution · Studio Retainer</div>
          <h2 className="font-display text-3xl mt-2 tracking-tight font-semibold"><SidebPill /> ran four parallel workstreams — diagnosis, remediation, program exit, and reserve release.</h2>
          <p className="mt-5 text-foreground/85 leading-relaxed text-lg">
            The Studio Retainer&apos;s value isn&apos;t a single deliverable — it&apos;s having an operator in the room who can move on all four workstreams simultaneously, with the credibility to speak directly to the PSP and the acquirer.
          </p>
          <ol className="mt-6 space-y-4 text-foreground/85 leading-relaxed">
            <li className="flex gap-3"><span className="font-semibold text-[#ac62ea] flex-shrink-0">01</span> <span><b>Root-cause analysis.</b> Audited the full payment stack — auth flow, BIN routing, 3DS coverage, velocity rules, BIN-attack patterns, refund/representment behavior — to surface the hidden contributors driving the non-friendly fraud rate above the 2% line.</span></li>
            <li className="flex gap-3"><span className="font-semibold text-[#ac62ea] flex-shrink-0">02</span> <span><b>Remediation & execution.</b> Designed and implemented an aggressive remediation plan — rule changes, 3DS step-up policy, velocity controls, decision-engine retraining, and merchant-side hygiene fixes. Worked alongside the merchant&apos;s fraud and engineering teams to ship and measure inside the program reporting window.</span></li>
            <li className="flex gap-3"><span className="font-semibold text-[#ac62ea] flex-shrink-0">03</span> <span><b>Compliance restoration.</b> Drove the fraud rate from <b>2% to under 0.5%</b>, then ran the formal exit conversation with the schemes and the acquirer — packaged the evidence, made the case, and got the merchant officially removed from the Visa/Mastercard fraud monitoring program.</span></li>
            <li className="flex gap-3"><span className="font-semibold text-[#ac62ea] flex-shrink-0">04</span> <span><b>Capital recovery.</b> Renegotiated reserve terms directly with the PSP, worked with the acquirer to schedule the systematic release of frozen funds, and re-anchored the commercial relationship around the merchant&apos;s restored standing — so the reserve change held beyond the immediate crisis.</span></li>
          </ol>
        </section>

        {/* Results */}
        <section id="results" className="scroll-mt-24" data-testid="proof-studio-results">
          <div className="label-mono">The results</div>
          <h2 className="font-display text-3xl mt-2 tracking-tight font-semibold">Fraud down 4x. Program exited. Reserve released. Relationship reset.</h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div className="card-sharp p-5 border-[#ac62ea]/30 bg-[#ac62ea]/[0.04]">
              <div className="label-mono">Fraud</div>
              <p className="mt-2 text-foreground/85 leading-relaxed text-sm"><b>Non-friendly fraud rate cut from 2% to under 0.5%</b> — a 4x reduction inside the program window, with the underlying rule and 3DS architecture left in place to prevent recurrence.</p>
            </div>
            <div className="card-sharp p-5 border-[#ac62ea]/30 bg-[#ac62ea]/[0.04]">
              <div className="label-mono">Compliance</div>
              <p className="mt-2 text-foreground/85 leading-relaxed text-sm"><b>Removed from the Visa/Mastercard fraud monitoring program.</b> Scheme fines stopped, mandatory reporting cadence ended, and elevated-risk treatment unwound across the stack.</p>
            </div>
            <div className="card-sharp p-5 border-[#ac62ea]/30 bg-[#ac62ea]/[0.04]">
              <div className="label-mono">Capital</div>
              <p className="mt-2 text-foreground/85 leading-relaxed text-sm"><b>Frozen reserve funds released on a scheduled basis</b> — operational capital returned to the merchant&apos;s balance sheet, easing the working-capital squeeze the aggressive reserve terms had created.</p>
            </div>
            <div className="card-sharp p-5 border-[#ac62ea]/30 bg-[#ac62ea]/[0.04]">
              <div className="label-mono">Strategic</div>
              <p className="mt-2 text-foreground/85 leading-relaxed text-sm"><b>Acquirer relationship reset to good standing</b> — with renegotiated reserve terms and a repaired commercial trust line for whatever scale moment came next.</p>
            </div>
          </div>
          <p className="mt-8 text-foreground/85 leading-relaxed text-lg">
            The crisis was the trigger — but the durable outcome was a fraud architecture, a scheme-relationship posture, and a reserve agreement that the merchant could now defend on their own.
          </p>
        </section>

        {/* Value Callout */}
        <section id="value" className="scroll-mt-24" data-testid="proof-studio-value">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0a1628] via-[#0a1628] to-[#2a1a5e] text-white p-8 lg:p-12">
            <div
              className="absolute inset-0 pointer-events-none opacity-25"
              aria-hidden
              style={{
                background:
                  "radial-gradient(70% 60% at 80% 30%, rgba(172,98,234,0.55) 0%, rgba(11,30,90,0) 70%)",
                filter: "blur(40px)",
              }}
            />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/15 bg-white/[0.05]">
                <Calculator className="h-3.5 w-3.5 text-[#c9a3f4]" />
                <span className="text-[11px] text-white/85 font-medium uppercase tracking-widest">Value summary</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl mt-5 tracking-[-0.03em] leading-[1.1] font-semibold">
                <span className="text-[#c9a3f4]">Capital back, fines stopped, scheme standing restored.</span> The Studio Retainer paid for itself in week one of the program exit.
              </h2>

              <div className="mt-8 grid sm:grid-cols-3 gap-4">
                <div className="rounded-xl border border-white/15 bg-white/[0.04] p-5" data-testid="proof-studio-value-fines">
                  <div className="label-mono text-white/60">Fines avoided</div>
                  <div className="font-display text-3xl mt-2 font-semibold tracking-tight">Compounding</div>
                  <div className="text-xs text-white/65 mt-1 leading-snug">Monthly scheme fines stopped at program exit — each additional cycle would have continued to compound.</div>
                </div>
                <div className="rounded-xl border border-white/15 bg-white/[0.04] p-5" data-testid="proof-studio-value-capital">
                  <div className="label-mono text-white/60">Capital unlocked</div>
                  <div className="font-display text-3xl mt-2 font-semibold tracking-tight">Reserve released</div>
                  <div className="text-xs text-white/65 mt-1 leading-snug">Operational capital returned to the balance sheet on a scheduled release negotiated with the acquirer.</div>
                </div>
                <div className="rounded-xl border border-white/15 bg-white/[0.04] p-5" data-testid="proof-studio-value-fraud">
                  <div className="label-mono text-white/60">Fraud rate</div>
                  <div className="font-display text-3xl mt-2 font-semibold tracking-tight">4x lower</div>
                  <div className="text-xs text-white/65 mt-1 leading-snug">2% → &lt;0.5% non-friendly fraud, with the architecture left in place to keep it there.</div>
                </div>
              </div>

              <div className="mt-8 rounded-xl border border-[#ac62ea]/40 bg-[#ac62ea]/[0.10] p-5">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-[#c9a3f4] flex-shrink-0 mt-0.5" />
                  <p className="text-white/90 leading-relaxed text-sm">
                    The Studio Retainer isn&apos;t scoped to a single deliverable — it&apos;s the operating partner you keep on the bench so when a crisis like this hits, the playbook starts running on day one. The retainer paid for itself many times over inside the first cycle.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final callout */}
        <section id="next" className="scroll-mt-24" data-testid="proof-studio-cta-section">
          <div className="rounded-2xl border border-border bg-white p-8 lg:p-10">
            <div className="label-mono">What this looks like for you</div>
            <h3 className="font-display text-2xl sm:text-3xl mt-2 tracking-tight font-semibold leading-snug">
              If your fraud rate is creeping, your acquirer&apos;s tone has shifted, or a reserve change just landed in your inbox — that&apos;s the engagement.
            </h3>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              The Studio Retainer keeps a senior payments operator on your bench monthly — for the program calls you&apos;d rather not run alone, the negotiations where credibility matters, and the architectural decisions that compound past the immediate fire.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button asChild className="btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] text-white h-11 px-6" data-testid="proof-studio-final-service">
                <Link to="/services/studio-retainer">
                  Explore the Studio Retainer <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="btn-pill h-11 px-6" data-testid="proof-studio-final-brief">
                <Link to="/inquiry">Send a custom brief</Link>
              </Button>
              <button
                type="button"
                onClick={() => openChat({ context: "Proof · Studio Retainer case study · Custom inquiry" })}
                className="btn-pill h-11 px-6 rounded-full text-foreground/70 hover:text-foreground text-sm font-medium underline-offset-4 hover:underline"
                data-testid="proof-studio-final-chat"
              >
                Or chat with us &rarr;
              </button>
            </div>
          </div>
        </section>

        <CaseStudyFaq testid="proof-studio-faq" schemaId="studio-retainer" items={FAQS} />

        {/* Cross-link */}
        <div className="text-center pt-4" data-testid="proof-studio-cross-link">
          <Link
            to="/proof/revenue-performance"
            className="inline-flex items-center gap-1.5 text-sm text-[#ac62ea] hover:text-[#8a48cf] font-medium"
          >
            Next case study: Payments Optimization Sprint — regulated remitter, +$18M recovered &rarr;
          </Link>
        </div>
      </div>
    </div>
  );
}
